import React from 'react';

import { Box, Divider } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router';

import { useAuthCredentials } from '@hrlogics/react-auth';
import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { appRoutes } from '@clearverify/constants';

import { Button, FullscreenLoader } from 'src/components/common';
import { TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useCurrentUserMfaTotpSetup, useUserMfaTotpUriKey } from 'src/hooks/auth';
import { useCurrentUser } from 'src/hooks/user';
import type { SxProp } from 'src/types/mui';

import { VerificationCodeFormField, VerificationCodeFormValues } from '../types';
import { verificationCodeFormValidationSchema } from '../utils/validation';

import { MfaSetupNote } from './MfaSetupNote';

type MfaVerificationCodeFormProps = {
  isImmediateVerification?: boolean;
  isAuthRoute?: boolean;
  isMfaRequired?: boolean;
};

export const MfaVerificationCodeForm: React.FC<MfaVerificationCodeFormProps> = ({
  isImmediateVerification,
  isAuthRoute,
  isMfaRequired,
}) => {
  const navigate = useNavigate();

  const { spawnNotification } = useNotification();

  const { accessToken } = useAuthCredentials();
  const { user } = useCurrentUser();

  const { uriKey, generating } = useUserMfaTotpUriKey(user?.email, accessToken);
  const { setupUserMfaByTotp } = useCurrentUserMfaTotpSetup();

  const handleFormSubmit = React.useCallback(
    async (formValues: VerificationCodeFormValues) => {
      if (!accessToken) return;
      try {
        await setupUserMfaByTotp(formValues.verificationCode, accessToken);
      } catch (error) {
        console.error(error);
        if (error instanceof Error) {
          spawnNotification(error.message, 'error');
        }
      }
    },
    [accessToken, setupUserMfaByTotp, spawnNotification],
  );

  const handleCopySetupCode = React.useCallback(
    (uriKey: string) => {
      // https://github.com/google/google-authenticator/wiki/Key-Uri-Format
      const setupUrl = new URL(uriKey);
      const setupUrlSearchParams = new URLSearchParams(setupUrl.search);

      const setupKeySecret = setupUrlSearchParams.get('secret');
      if (!setupKeySecret) return;

      // eslint-disable-next-line promise/catch-or-return
      navigator.clipboard.writeText(setupKeySecret).then(
        () => {
          spawnNotification(
            'Setup Key has been copied to clipboard',
            'success',
            'Copied successfully',
          );
        },
        () => {
          spawnNotification('Link not copied', 'error', 'Error');
        },
      );
    },
    [spawnNotification],
  );

  const handleLaterSetup = React.useCallback(() => {
    navigate(appRoutes.root);
  }, [navigate]);

  if (generating) {
    return <FullscreenLoader />;
  }

  return (
    <Form<VerificationCodeFormValues>
      onSubmit={handleFormSubmit}
      subscription={{ submitting: true, valid: true }}
      validate={validateWithYupSchema(verificationCodeFormValidationSchema)}
    >
      {({ submitting, valid }) => (
        <React.Fragment>
          <MfaSetupNote
            isImmediateVerification={isImmediateVerification}
            isAuthRoute={isAuthRoute}
            isMfaRequired={isMfaRequired}
          />

          {uriKey && (
            <Box mt={4} sx={codeContainerBoxStyles}>
              <Box sx={qrCodeBoxStyles}>
                <QRCodeSVG value={uriKey} size={150} />
              </Box>
              <Divider orientation="vertical" variant="middle" flexItem />
              <Box>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleCopySetupCode(uriKey)}
                >
                  Copy setup key
                </Button>
              </Box>
            </Box>
          )}

          <Box mt={4}>
            <Field
              name={VerificationCodeFormField.verificationCode}
              component={TextField}
              label="Verification Code"
              fullWidth
              required
              disabled={submitting}
              slotProps={{
                input: {
                  inputMode: 'numeric',
                  autoComplete: 'one-time-code',
                },
              }}
            />
          </Box>

          <Box display="flex" mt={4} gap={2} justifyContent="center">
            {!isMfaRequired && isAuthRoute && (
              <Button
                type="button"
                color="primary"
                variant="outlined"
                onClick={handleLaterSetup}
                disabled={submitting}
                loading={submitting}
              >
                Later
              </Button>
            )}

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={submitting || !valid}
              loading={submitting}
            >
              Submit
            </Button>
          </Box>
        </React.Fragment>
      )}
    </Form>
  );
};

const codeContainerBoxStyles: SxProp = theme => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 5,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: 0,
  },
});

const qrCodeBoxStyles: SxProp = {
  display: 'inline-block',
  p: 2,

  fontSize: 0,
  borderRadius: '10px',
  border: '1px solid rgba(0, 0, 0, 0.1)',
};
